import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, MagnifyingGlass, DownloadSimple, Trash, FunnelSimple, UserSwitch, Archive, CaretUp, CaretDown, TrashSimple } from "@phosphor-icons/react";
import AddLeadModal from "../components/crm/AddLeadModal";
import AddActivityModal from "../components/crm/AddActivityModal";
import BulkActionBar from "../components/crm/BulkActionBar";
import ReassignDialog from "../components/crm/ReassignDialog";
import ConfirmDialog from "../components/crm/ConfirmDialog";
import { StatusBadge, PriorityBadge } from "../components/crm/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtCurrency, fmtDate } from "../lib/utils";
import { COUNTRIES, LEAD_SOURCES } from "../lib/constants";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import {
  loadPrefs, savePrefs, clearPrefs,
  loadSession, saveSession, clearSession,
} from "../lib/tableMemory";

// UI-STABILITY-1: defaults used as baseline + on Reset filters.
const DEFAULT_FILTERS = {
  status: "", priority: "", agent_id: "", country: "", buyer_persona: "",
  lead_source: "", search: "", created_range: "all", followup_range: "all", stale: "all",
};
const DEFAULT_SORT = { key: "updated_at", dir: "desc" };

// Allow-lists for restored values; anything outside is dropped on load.
const VALID_PRIORITY = new Set(["", "A", "B", "C"]);
const VALID_CREATED_RANGE = new Set(["all", "today", "week", "month"]);
const VALID_FOLLOWUP_RANGE = new Set(["all", "today", "overdue", "next7", "next30", "none"]);
const VALID_STALE = new Set(["all", "14", "30", "90"]);
const VALID_SORT_KEYS = new Set([
  null,
  "company_name", "contact_name", "country", "city", "lead_source",
  "buyer_persona", "assigned_agent", "lead_status", "priority",
  "revenue", "updated_at", "created_at",
  "last_activity_date", "operational_follow_up_date",
]);

function sanitizeRestoredFilters(saved, user) {
  if (!saved || typeof saved !== "object") return null;
  const f = { ...DEFAULT_FILTERS };
  const str = (v) => (typeof v === "string" ? v : "");
  f.status = str(saved.status);
  f.priority = VALID_PRIORITY.has(saved.priority) ? saved.priority : "";
  f.country = str(saved.country);
  f.buyer_persona = str(saved.buyer_persona);
  f.lead_source = str(saved.lead_source);
  f.created_range = VALID_CREATED_RANGE.has(saved.created_range)
    ? saved.created_range : "all";
  f.followup_range = VALID_FOLLOWUP_RANGE.has(saved.followup_range)
    ? saved.followup_range : "all";
  f.stale = VALID_STALE.has(saved.stale) ? saved.stale : "all";
  // Admin-only filter: drop for agents.
  f.agent_id = user?.role === "admin" ? str(saved.agent_id) : "";
  return f;
}

function sanitizeRestoredSort(saved) {
  if (!saved || typeof saved !== "object") return null;
  const key = saved.key === null || VALID_SORT_KEYS.has(saved.key) ? saved.key : null;
  const dir = saved.dir === "asc" ? "asc" : "desc";
  return { key, dir };
}

function todayRange() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const end = new Date(d); end.setDate(end.getDate() + 1);
  return [d.toISOString(), end.toISOString()];
}
function thisWeekRange() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  const end = new Date(d); end.setDate(end.getDate() + 7);
  return [d.toISOString(), end.toISOString()];
}
function thisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return [start.toISOString(), end.toISOString()];
}
function nextNDays(n) {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const end = new Date(d); end.setDate(end.getDate() + n);
  return [d.toISOString(), end.toISOString()];
}

export default function LeadsList() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [enums, setEnums] = useState({ lead_statuses: [], buyer_personas: [], priorities: [] });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showAdd, setShowAdd] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [reassignOpen, setReassignOpen] = useState(false);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkHardDeleteOpen, setBulkHardDeleteOpen] = useState(false);
  const [singleArchiveLead, setSingleArchiveLead] = useState(null);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingActivityLeadId, setEditingActivityLeadId] = useState(null);
  const [restored, setRestored] = useState(false);

  const isAdmin = user?.role === "admin";

  // UI-STABILITY-1: restore persisted filters/sort/search once we know the user.
  useEffect(() => {
    if (!user || restored) return;
    const savedPrefs = loadPrefs(user, "leads");
    const cleanedFilters = sanitizeRestoredFilters(savedPrefs?.filters, user);
    const cleanedSort = sanitizeRestoredSort(savedPrefs?.sort);
    const savedSession = loadSession(user, "leads");
    const savedSearch =
      savedSession && typeof savedSession.search === "string"
        ? savedSession.search
        : "";
    setFilters((prev) => ({
      ...(cleanedFilters || prev),
      search: savedSearch,
    }));
    if (cleanedSort) setSort(cleanedSort);
    setRestored(true);
  }, [user, restored]);

  // UI-STABILITY-1: persist filters (excluding search) + sort whenever they change.
  useEffect(() => {
    if (!user || !restored) return;
    // Search is intentionally excluded from localStorage and lives only in
    // sessionStorage so it does not become a long-term hidden filter.
    const { search: _ignored, ...persist } = filters;
    void _ignored;
    savePrefs(user, "leads", { filters: persist, sort });
  }, [user, restored, filters, sort]);

  // UI-STABILITY-1: persist search text to sessionStorage only.
  useEffect(() => {
    if (!user || !restored) return;
    saveSession(user, "leads", { search: filters.search || "" });
  }, [user, restored, filters.search]);

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSort(DEFAULT_SORT);
    clearPrefs(user, "leads");
    clearSession(user, "leads");
  };

  const agentsMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a])), [agents]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      ["status", "priority", "agent_id", "country", "buyer_persona", "lead_source", "search"].forEach((k) => {
        if (filters[k]) params[k] = filters[k];
      });
      // Created date range
      if (filters.created_range === "today") {
        const [a, b] = todayRange(); params.created_from = a; params.created_to = b;
      } else if (filters.created_range === "week") {
        const [a, b] = thisWeekRange(); params.created_from = a; params.created_to = b;
      } else if (filters.created_range === "month") {
        const [a, b] = thisMonthRange(); params.created_from = a; params.created_to = b;
      }
      // Follow-up range
      if (filters.followup_range === "today") {
        const [a, b] = todayRange(); params.follow_up_from = a; params.follow_up_to = b;
      } else if (filters.followup_range === "overdue") {
        params.follow_up_to = new Date().toISOString();
      } else if (filters.followup_range === "next7") {
        const [a, b] = nextNDays(7); params.follow_up_from = a; params.follow_up_to = b;
      } else if (filters.followup_range === "next30") {
        const [a, b] = nextNDays(30); params.follow_up_from = a; params.follow_up_to = b;
      } else if (filters.followup_range === "none") {
        params.no_follow_up = true;
      }
      // Stale
      if (filters.stale === "14") params.stale_days = 14;
      else if (filters.stale === "30") params.stale_days = 30;
      else if (filters.stale === "90") params.stale_days = 90;
      const [l, u, e] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/users"),
        api.get("/meta/enums"),
      ]);
      setLeads(l.data); setAgents(u.data); setEnums(e.data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // UI-STABILITY-1B: wait for persisted prefs to be restored before
    // running the first fetch so we never flash an unfiltered/default
    // table on navigation or refresh.
    if (!restored) return;
    setSelected(new Set()); // clear selection when filters change
    load();
    /* eslint-disable-next-line */
  }, [filters, restored]);

  const countries = useMemo(
    () => {
      const fromData = new Set(leads.map((l) => l.country).filter(Boolean));
      // Show union: canonical list + any legacy values still present in data
      const merged = new Set([...COUNTRIES, ...fromData]);
      return Array.from(merged).sort();
    },
    [leads]
  );

  // UI-STABILITY-1B-tier1: memoize the agent dropdown source to avoid
  // re-filtering on unrelated re-renders.
  const agentOptions = useMemo(
    () => agents.filter((a) => a.role === "agent"),
    [agents]
  );

  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead archived");
      setSingleArchiveLead(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  };

  const exportCsv = () => {
    // Phase 3E: pass current filters through to backend for filtered export
    const params = new URLSearchParams();
    ["status", "priority", "agent_id", "country", "buyer_persona", "lead_source", "search"].forEach((k) => {
      if (filters[k]) params.set(k, filters[k]);
    });
    const qs = params.toString();
    window.open(`${API_BASE}/leads/csv/export${qs ? `?${qs}` : ""}`, "_blank");
  };

  const exportSelected = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(",");
    window.open(`${API_BASE}/leads/csv/export?lead_ids=${encodeURIComponent(ids)}`, "_blank");
  };

  const onSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  };

  const sortedLeads = useMemo(() => {
    if (!sort.key) return leads;
    const accessor = (l) => {
      switch (sort.key) {
        case "company_name": return (l.company_name || "").toLowerCase();
        case "contact_name": return (l.contact_name || "").toLowerCase();
        case "country": return (l.country || "").toLowerCase();
        case "city": return (l.city || "").toLowerCase();
        case "lead_source": return (l.lead_source || "").toLowerCase();
        case "buyer_persona": return (l.buyer_persona || "").toLowerCase();
        case "assigned_agent": return (agentsMap[l.assigned_agent_id]?.name || "").toLowerCase();
        case "lead_status": return l.lead_status || "";
        case "priority": return l.priority || "";
        case "revenue": return l.revenue || 0;
        case "updated_at": return l.updated_at || "";
        case "created_at": return l.created_at || "";
        case "last_activity_date": return l.last_activity_date || "";
        case "operational_follow_up_date": return l.operational_follow_up_date || "";
        default: return "";
      }
    };
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...leads].sort((a, b) => {
      const va = accessor(a), vb = accessor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [leads, sort, agentsMap]);

  const SortHeader = ({ label, k }) => (
    <button onClick={() => onSort(k)} data-testid={`sort-leads-${k}`}
      className="inline-flex items-center gap-1 hover:text-[#1A3A21] uppercase tracking-wider">
      {label}
      {sort.key === k && (
        sort.dir === "asc"
          ? <CaretUp size={11} weight="bold" />
          : <CaretDown size={11} weight="bold" />
      )}
    </button>
  );

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(leads.map((l) => l.id)));
  };

  const bulkReassign = async (agentId) => {
    try {
      const { data } = await api.post("/leads/bulk/reassign", {
        lead_ids: Array.from(selected),
        assigned_agent_id: agentId,
      });
      toast.success(`Reassigned ${data.updated} leads`);
      setReassignOpen(false);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    }
  };

  const bulkArchive = async () => {
    try {
      const { data } = await api.post("/leads/bulk/archive", { lead_ids: Array.from(selected) });
      toast.success(`Archived ${data.archived} leads`);
      setSelected(new Set());
      setBulkArchiveOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  };

  const bulkHardDelete = async () => {
    try {
      const { data } = await api.post("/leads/bulk/hard-delete", { lead_ids: Array.from(selected) });
      toast.success(
        `Permanently deleted ${data.deleted_leads} lead${data.deleted_leads === 1 ? "" : "s"} ` +
        `and ${data.deleted_activities} related activit${data.deleted_activities === 1 ? "y" : "ies"}`
      );
      setSelected(new Set());
      setBulkHardDeleteOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Permanent delete failed");
    }
  };

  const headerCols = [
    { label: "Code", key: null },
    { label: "Company", key: "company_name" },
    { label: "Persona", key: "buyer_persona" },
    { label: "Contact", key: "contact_name" },
    { label: "Phone", key: null },
    { label: "Status", key: "lead_status" },
    { label: "Priority", key: "priority" },
    { label: "Country", key: "country" },
    { label: "Revenue", key: "revenue" },
    { label: "Last Activity", key: null },
    { label: "Last Activity Date", key: "last_activity_date" },
    { label: "Follow-Up", key: "operational_follow_up_date" },
    ...(isAdmin
      ? [
          { label: "Assigned", key: "assigned_agent" },
          { label: "Created Date", key: "created_at" },
        ]
      : []),
    { label: "", key: null },
  ];

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Leads</div>
          <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-1">
            {user?.role === "admin" ? "All leads" : "My leads"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
            <FunnelSimple size={16} /> Filters
          </button>
          <button onClick={resetFilters} data-testid="reset-filters"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 text-gray-600">
            Reset filters
          </button>
          {user?.role === "admin" && (
            <>
              <button onClick={exportCsv} data-testid="export-csv-button"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
                <DownloadSimple size={16} /> Export CSV
              </button>
              <button onClick={() => setShowAdd(true)} data-testid="add-lead-button"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-[#E1A104] text-[#1A3A21] hover:bg-[#C98F03]">
                <Plus size={16} weight="bold" /> New Lead
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="md:col-span-2 lg:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="leads-search-input"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search code, company, contact, email..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {enums.lead_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="A">Priority A</SelectItem>
              <SelectItem value="B">Priority B</SelectItem>
              <SelectItem value="C">Priority C</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
            <Select value={filters.buyer_persona || "all"} onValueChange={(v) => setFilters({ ...filters, buyer_persona: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-persona"><SelectValue placeholder="Buyer Persona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All personas</SelectItem>
                {enums.buyer_personas.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.lead_source || "all"} onValueChange={(v) => setFilters({ ...filters, lead_source: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Lead Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {user?.role === "admin" && (
              <Select value={filters.agent_id || "all"} onValueChange={(v) => setFilters({ ...filters, agent_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  <SelectItem value="_unassigned">Unassigned</SelectItem>
                  {agentOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={filters.country || "all"} onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.created_range} onValueChange={(v) => setFilters({ ...filters, created_range: v })}>
              <SelectTrigger data-testid="filter-created"><SelectValue placeholder="Created" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Created — All</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This week</SelectItem>
                <SelectItem value="month">This month</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.followup_range} onValueChange={(v) => setFilters({ ...filters, followup_range: v })}>
              <SelectTrigger data-testid="filter-followup"><SelectValue placeholder="Follow-up" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Follow-up — All</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="next7">Next 7 days</SelectItem>
                <SelectItem value="next30">Next 30 days</SelectItem>
                <SelectItem value="none">No follow-up scheduled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.stale} onValueChange={(v) => setFilters({ ...filters, stale: v })}>
              <SelectTrigger data-testid="filter-stale"><SelectValue placeholder="Stale" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Stale — All</SelectItem>
                <SelectItem value="14">No activity 14+ days</SelectItem>
                <SelectItem value="30">No activity 30+ days</SelectItem>
                <SelectItem value="90">No activity 90+ days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isAdmin && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
          <button onClick={() => setReassignOpen(true)} data-testid="bulk-reassign-leads"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#E1A104] text-[#1A3A21] hover:bg-[#C98F03] text-xs font-semibold">
            <UserSwitch size={14} weight="bold" /> Reassign
          </button>
          <button onClick={exportSelected} data-testid="bulk-export-leads"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-semibold">
            <DownloadSimple size={14} weight="bold" /> Export Selected
          </button>
          <button onClick={() => setBulkArchiveOpen(true)} data-testid="bulk-archive-leads"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-semibold">
            <Archive size={14} weight="bold" /> Archive
          </button>
          <button onClick={() => setBulkHardDeleteOpen(true)} data-testid="bulk-hard-delete-leads"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold">
            <TrashSimple size={14} weight="bold" /> Delete permanently
          </button>
        </BulkActionBar>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1400px]" data-testid="leads-table">
          <thead className="bg-gray-50">
            <tr>
              {isAdmin && (
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    data-testid="leads-select-all"
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer" />
                </th>
              )}
              {headerCols.map((h, i) => (
                <th key={`col-${h.key || h.label || i}`} className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                  {h.key ? <SortHeader label={h.label} k={h.key} /> : h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={headerCols.length + (isAdmin ? 1 : 0)} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : sortedLeads.length === 0 ? (
              <tr><td colSpan={headerCols.length + (isAdmin ? 1 : 0)} className="text-center py-10 text-gray-500">No leads found.</td></tr>
            ) : sortedLeads.map((l) => (
              <tr key={l.id} className={`border-t border-gray-100 hover:bg-gray-50 ${selected.has(l.id) ? "bg-yellow-50/40" : ""}`}>
                {isAdmin && (
                  <td className="py-3 px-4">
                    <input type="checkbox" checked={selected.has(l.id)}
                      onChange={() => toggleOne(l.id)}
                      data-testid={`lead-select-${l.id}`}
                      className="w-4 h-4 rounded border-gray-300 cursor-pointer" />
                  </td>
                )}
                <td className="py-3 px-4">
                  <span className="font-mono text-[11px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded" data-testid={`lead-code-${l.id}`}>
                    {l.lead_code || "—"}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <Link to={`/leads/${l.id}`} className="text-sm font-semibold text-gray-900 hover:text-[#1A3A21]"
                    data-testid={`lead-row-${l.id}`}>
                    {l.company_name}
                  </Link>
                  <div className="text-[11px] text-gray-500">{l.lead_source || "—"}</div>
                </td>
                <td className="py-3 px-4 text-sm text-gray-700" data-testid={`lead-persona-${l.id}`}>
                  {l.buyer_persona || "—"}
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm text-gray-900">{l.contact_name || "—"}</div>
                  <div className="text-[11px] text-gray-500">{l.email}</div>
                </td>
                <td className="py-3 px-4 text-sm text-gray-700" data-testid={`lead-phone-${l.id}`}>
                  {l.phone ? (
                    <a
                      href={`tel:${l.phone}`}
                      className="text-gray-700 hover:text-[#1A3A21] hover:underline"
                      data-testid={`lead-phone-link-${l.id}`}
                    >
                      {l.phone}
                    </a>
                  ) : "—"}
                </td>
                <td className="py-3 px-4"><StatusBadge status={l.lead_status} /></td>
                <td className="py-3 px-4"><PriorityBadge priority={l.priority} /></td>
                <td className="py-3 px-4 text-sm text-gray-700">{l.country || "—"}</td>
                <td className="py-3 px-4 text-sm font-semibold text-gray-900" data-testid={`lead-revenue-${l.id}`}>{fmtCurrency(l.revenue || 0)}</td>
                <td className="py-3 px-4 text-sm" data-testid={`lead-last-activity-type-${l.id}`}>
                  {l.last_activity_type ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!l.last_activity) return;
                        setEditingActivity(l.last_activity);
                        setEditingActivityLeadId(l.id);
                      }}
                      data-testid={`lead-last-activity-type-btn-${l.id}`}
                      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider bg-gray-100 text-gray-700 hover:bg-[#1A3A21] hover:text-white transition-colors"
                    >
                      {l.last_activity_type}
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-gray-500" data-testid={`lead-last-activity-date-${l.id}`}>
                  {l.last_activity_date ? fmtDate(l.last_activity_date) : "—"}
                </td>
                <td className="py-3 px-4 text-sm" data-testid={`lead-followup-date-${l.id}`}>
                  {l.operational_follow_up_date ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!l.operational_follow_up_activity) return;
                        setEditingActivity(l.operational_follow_up_activity);
                        setEditingActivityLeadId(l.id);
                      }}
                      data-testid={`lead-followup-btn-${l.id}`}
                      className={`hover:underline ${
                        l.operational_follow_up_is_overdue
                          ? "text-red-600 font-semibold"
                          : "text-gray-700"
                      }`}
                    >
                      {fmtDate(l.operational_follow_up_date)}
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                {isAdmin && (
                  <td className="py-3 px-4 text-sm text-gray-700">
                    {l.assigned_agent_id ? agentsMap[l.assigned_agent_id]?.name || "—" : <span className="text-gray-400 italic">Unassigned</span>}
                  </td>
                )}
                {isAdmin && (
                  <td className="py-3 px-4 text-sm text-gray-500" data-testid={`lead-created-date-${l.id}`}>
                    {l.created_at ? fmtDate(l.created_at) : "—"}
                  </td>
                )}
                <td className="py-3 px-4 text-right">
                  {user?.role === "admin" && (
                    <button onClick={() => setSingleArchiveLead(l)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                      data-testid={`delete-lead-${l.id}`}>
                      <Trash size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddLeadModal open={showAdd} onOpenChange={setShowAdd} agents={agents} onCreated={load} />
      <AddActivityModal
        open={!!editingActivity}
        onOpenChange={(o) => {
          if (!o) {
            setEditingActivity(null);
            setEditingActivityLeadId(null);
          }
        }}
        leadId={editingActivityLeadId}
        defaultType={editingActivity?.activity_type}
        editing={editingActivity}
        onCreated={() => {
          setEditingActivity(null);
          setEditingActivityLeadId(null);
          load();
        }}
        onDeleted={() => {
          setEditingActivity(null);
          setEditingActivityLeadId(null);
          load();
        }}
      />
      <ReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        agents={agents}
        count={selected.size}
        kind="leads"
        onConfirm={bulkReassign}
      />
      <ConfirmDialog
        open={bulkArchiveOpen}
        onOpenChange={setBulkArchiveOpen}
        title="Archive leads"
        confirmLabel="Archive"
        message={`You are about to archive ${selected.size} lead${selected.size === 1 ? "" : "s"}. This will also remove all connected activities from active operational views and from dashboard/report counts. The records will not be permanently deleted. Continue?`}
        onConfirm={bulkArchive}
      />
      <ConfirmDialog
        open={bulkHardDeleteOpen}
        onOpenChange={setBulkHardDeleteOpen}
        title="Delete permanently"
        confirmLabel="Delete permanently"
        destructive
        message={`Permanent delete will remove ${selected.size} selected lead${selected.size === 1 ? "" : "s"} and their related activities. This cannot be undone.`}
        onConfirm={bulkHardDelete}
      />
      <ConfirmDialog
        open={!!singleArchiveLead}
        onOpenChange={(o) => { if (!o) setSingleArchiveLead(null); }}
        title="Archive lead"
        confirmLabel="Archive"
        message={`You are about to archive ${singleArchiveLead?.company_name || "this lead"}. This will also remove all connected activities from active operational views and from dashboard/report counts. The record will not be permanently deleted. Continue?`}
        onConfirm={() => singleArchiveLead && deleteLead(singleArchiveLead.id)}
      />
    </div>
  );
}
